export const FMX_FIELD_TYPE_MAP = {
  'Text':        { label: 'Text',       category: 'string'  },
  'URL':         { label: 'URL',        category: 'string'  },
  'Date':        { label: 'Date',       category: 'date'    },
  'DropDownList':{ label: 'Dropdown',   category: 'string'  },
  'ReadOnly':    { label: 'Read only',  category: 'string'  },
  'Number':      { label: 'Number',     category: 'number'  },
  'Checkbox':    { label: 'Yes/No',     category: 'boolean' },
  'Time':        { label: 'Time',       category: 'string'  },
  'Attachments': { label: 'Attachment', category: 'string'  },
  'Currency':    { label: 'Currency',   category: 'number'  },
  'User':        { label: 'User',       category: 'string'  },
};

export function getFieldTypeLabel(fieldType) {
  return FMX_FIELD_TYPE_MAP[fieldType]?.label || fieldType || 'Text';
}

// Normalize a time-of-day value to the "HH:mm:ss" TimeSpan string the FMX API
// requires (e.g. building operating hours are OpenAPI format "date-span" and
// reject anything a .NET TimeSpan can't parse). Returns null if unparseable.
// Accepts 24-hour ("17:30", "8:00:00"), 12-hour with AM/PM ("8:00 AM", "5.30pm"),
// bare hours ("8"), and Excel day fractions (0.3333 → "08:00:00").
export function parseTimeToHMS(value) {
  if (value === null || value === undefined || value === '') return null;
  const str = String(value).trim();

  // Excel stores times as fractions of a day; CSV exports can leak the raw serial.
  if (/^0?\.\d+$/.test(str) || (typeof value === 'number' && value > 0 && value < 1)) {
    const totalMinutes = Math.round(Number(str) * 24 * 60);
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
  }
  const m = str.match(/^(\d{1,2})(?:[:.](\d{1,2}))?(?:[:.](\d{1,2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i);
  if (!m) return null;

  let hour = parseInt(m[1], 10);
  const minute = m[2] !== undefined ? parseInt(m[2], 10) : 0;
  const second = m[3] !== undefined ? parseInt(m[3], 10) : 0;
  const meridiem = m[4] ? m[4].toLowerCase().charAt(0) : null;

  if (minute > 59 || second > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === 'p' && hour !== 12) hour += 12;
    if (meridiem === 'a' && hour === 12) hour = 0;
  } else if (hour > 23) {
    return null;
  }

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
}

export function getFieldTypeCategory(fieldType) {
  return FMX_FIELD_TYPE_MAP[fieldType]?.category || 'string';
}

// Parse a spreadsheet cell into a real boolean. The FMX API's boolean
// converter accepts only true/false (or "true"/"false") — human-entered
// Yes/No/Y/N/1/0/X cells must be coerced before push. Returns undefined
// when the value isn't recognizably boolean so callers can fail visibly.
const BOOLEAN_CELL_VALUES = {
  'true': true, 'yes': true, 'y': true, '1': true, 'x': true,
  'false': false, 'no': false, 'n': false, '0': false,
};
export function parseBooleanCell(value) {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined) return undefined;
  return BOOLEAN_CELL_VALUES[String(value).trim().toLowerCase()];
}

// Split a delimited cell ("A; B, C") into trimmed non-empty values.
export function splitMultiValue(value) {
  if (value === null || value === undefined) return [];
  return String(value)
    .split(/[;,]/)
    .map(s => s.trim())
    .filter(Boolean);
}

// Coerce a raw cell/OCR value into the JSON shape the FMX API accepts for a
// custom field of the given fieldType. Returns null when there is no usable
// value. Shared by the CSV push transform and the equipment scan writer.
export function coerceCustomFieldValue(value, fieldType) {
  if (value === null || value === undefined || value === '') return null;
  const category = getFieldTypeCategory(fieldType);
  switch (category) {
    case 'number': {
      const cleaned = String(value).replace(/[^0-9.-]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    }
    case 'boolean':
      return value === true || value === 'true' ||
             value === '1' ||
             String(value).toLowerCase() === 'yes';
    case 'date': {
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toISOString();
    }
    case 'string':
    default:
      return String(value);
  }
}

export function getTooltipText(field) {
  if (!field) return '';
  const baseType = field.isCustomField
    ? getFieldTypeLabel(field.fieldType)
    : {
        string: 'Text',
        number: 'Number',
        date: 'Date',
        email: 'Email',
        time: 'Time',
        boolean: 'Yes/No',
        stringArray: 'List',
      }[field.type] || 'Text';

  const format = {
    'Text':     'any characters',
    'URL':      'valid URL (e.g. https://example.com)',
    'Date':     'MM/DD/YYYY or YYYY-MM-DD',
    'Dropdown': 'one of the available options',
    'Number':   'numeric value (e.g. 1956 or 3.14)',
    'Yes/No':   'Yes or No',
    'Time':     'HH:MM (e.g. 09:30)',
    'Currency': 'numeric value (e.g. 25.00)',
    'Email':    'valid email (e.g. name@example.com)',
    'List':     'one or more values separated by ; or ,',
  }[baseType] || 'any value';

  const suffix = field.isCustomField ? ' · FMX Custom Field' : '';
  return `${baseType} — ${format}${suffix}`;
}
